import addDays from 'date-fns/addDays';
import { utcDay } from 'd3-time';
import { utcFormat, timeFormat } from 'd3-time-format';

export default formatDate;

export type PeriodKey =
  | 'day'
  | 'day-short'
  | 'day-full'
  | 'day-full-raw'
  | 'week'
  | 'week-year'
  | 'weekEnd'
  | 'month'
  | 'year'
  | 'long';

/**
 * See https://github.com/d3/d3-time-format#locale_format for more
 * information about how to specify date formatting options.
 */
const longFormat: { [key: string]: string } = {
  day: '%a, %b %-d',
  'day-short': '%b %-d, %Y',
  'day-full': '%a, %b %-d, %Y',
  'day-full-raw': '%B %d %Y',
  week: '%b %-d',
  'week-year': '%b %-d, %Y',
  weekEnd: '%-d',
  month: '%B ’%y',
  year: '%Y',
  long: '%A, %B %-d, %Y',
};

const shortFormat: { [key: string]: string } = {
  day: '%b %e',
  week: '%b, ’%y',
  month: '%b, ’%y',
  year: '%Y',
};

/**
 * formatDate formats Date objects for human consumption.
 *
 * @param {Date} date a Date object
 * @param 'day'|'day-short'| 'day-full' |'week'|'week-year'|'weekEnd'|'month'|'year'|'long'|undefined - [period='day'] The format you want to have returned
 * @param {true|false|undefined} [short=false] Should this be a shortened version of the date?
 * @param {true|false|} [withRounding=true] For some dates we don't need rounding. Example - Concerts table
 * @returns {string} Date formatted for human consumption.
 */
function formatDate(
  date: Date | null,
  period: PeriodKey = 'day',
  short = false,
  withRounding = true,
  utc = true
): string {
  if (!date) return '–';

  const configurations = short ? shortFormat : longFormat;
  const format = utc
    ? utcFormat(configurations[period])
    : timeFormat(configurations[period]);
  const start = withRounding ? utcDay.round(date) : date;

  if (period === 'week' || period === 'week-year') {
    const end = addDays(date, 6);
    return `${format(start)} - ${format(utcDay.round(end))}`;
  }

  return format(start);
}
